import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in Leaflet with bundlers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const s = {
  mapContainer: {
    width: '100%',
    height: '400px',
    borderRadius: '12px',
    overflow: 'hidden',
    border: '1px solid #1e2a36',
  },
};

/**
 * Leaflet map showing a user's location with a marker.
 * Also draws a polyline trail if history is provided.
 *
 * Props:
 *   latitude   — latest latitude
 *   longitude  — latest longitude
 *   history    — array of { latitude, longitude, recordedAt }
 *   username   — user being tracked (for popup label)
 */
export default function LocationMap({ latitude, longitude, history = [], username = '' }) {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markerRef = useRef(null);
  const trailRef = useRef(null);

  // Initialize the map once.
  useEffect(() => {
    if (mapInstance.current || !mapRef.current) return;

    const map = L.map(mapRef.current).setView([latitude || 0, longitude || 0], 13);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(map);

    mapInstance.current = map;

    // Fix: Leaflet needs invalidateSize() when the container
    // is rendered inside a modal — the container dimensions
    // aren't ready when the map first initializes.
    setTimeout(() => {
      map.invalidateSize();
    }, 100);

    return () => {
      map.remove();
      mapInstance.current = null;
      markerRef.current = null;
      trailRef.current = null;
    };
  }, []);

  // Update marker and trail when location changes.
  useEffect(() => {
    const map = mapInstance.current;
    if (!map || latitude == null || longitude == null) return;

    // Update or create the latest-position marker.
    if (markerRef.current) {
      markerRef.current.setLatLng([latitude, longitude]);
    } else {
      markerRef.current = L.marker([latitude, longitude]).addTo(map);
    }
    markerRef.current.bindPopup(
      `<b>${username}</b><br/>Lat: ${latitude.toFixed(6)}<br/>Lng: ${longitude.toFixed(6)}`
    ).openPopup();

    // Draw the trail if history is provided.
    if (trailRef.current) {
      map.removeLayer(trailRef.current);
      trailRef.current = null;
    }

    if (history.length > 1) {
      const points = history
        .slice()
        .reverse() // oldest first for the polyline
        .map(h => [h.latitude, h.longitude]);
      trailRef.current = L.polyline(points, {
        color: '#00e5ff',
        weight: 3,
        opacity: 0.7,
      }).addTo(map);
    }

    // Pan to the latest position and invalidate size to
    // ensure tiles render correctly inside the modal.
    map.setView([latitude, longitude], 13);
    setTimeout(() => map.invalidateSize(), 100);
  }, [latitude, longitude, history, username]);

  return <div ref={mapRef} style={s.mapContainer} />;
}
